import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Luggage, Users } from "lucide-react";
import { VEHICLES } from "@/lib/data";
import { useLang } from "@/lib/i18n";

const ease = [0.22, 1, 0.36, 1];
const ORDER = ["business", "first", "van", "standard"];

const FleetCard = ({ id, index }) => {
  const { t } = useLang();
  const vehicle = VEHICLES[id];

  return (
    <motion.div
      initial={{ opacity: 0, y: 44 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-90px" }}
      transition={{ duration: 0.8, delay: index * 0.12, ease }}
      className="h-full"
    >
      <Link
        to="/flotte"
        data-testid={`home-fleet-card-${id}`}
        className="group relative block h-full overflow-hidden rounded-[2rem] border border-ink/10 bg-white p-6 shadow-[0_30px_80px_-42px_rgba(11,31,77,0.35)] transition-[border-color,box-shadow,transform] duration-500 hover:-translate-y-2 hover:border-miles/35 hover:shadow-[0_42px_110px_-44px_rgba(36,87,255,0.35)] sm:p-8"
      >
        <div className="pointer-events-none absolute inset-x-8 top-8 h-56 rounded-full bg-miles-ice opacity-80 blur-3xl transition-opacity duration-700 group-hover:opacity-100" />
        <div className="relative flex items-start justify-between gap-4">
          <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.28em] text-miles">
            {String(index + 1).padStart(2, "0")}
          </span>
          <span className="rounded-full border border-ink/10 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.18em] text-slate transition-colors duration-300 group-hover:border-miles/30 group-hover:text-miles">
            MILES
          </span>
        </div>

        <div className="relative mt-8 flex h-52 items-end justify-center overflow-visible sm:h-64 lg:h-72">
          <div className="absolute bottom-2 h-20 w-[82%] rounded-[100%] bg-miles-deep/10 blur-xl transition-transform duration-700 group-hover:scale-x-110" />
          <img
            src={vehicle.image}
            alt={t(`homeFleet.items.${id}.model`)}
            loading="lazy"
            className="relative h-full w-full object-contain transition-transform duration-700 ease-out group-hover:-translate-y-3 group-hover:scale-[1.035]"
          />
        </div>

        <div className="relative mt-8 border-t border-ink/10 pt-6">
          <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-slate">{t(`homeFleet.items.${id}.model`)}</p>
          <div className="mt-2 flex items-end justify-between gap-4">
            <h3 className="text-3xl font-semibold tracking-[-0.04em] text-ink sm:text-4xl">{t(`homeFleet.items.${id}.name`)}</h3>
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-ink text-white transition-[background-color,transform] duration-300 group-hover:-translate-y-0.5 group-hover:bg-miles">
              <ArrowUpRight className="h-4 w-4" strokeWidth={1.5} />
            </span>
          </div>
          <p className="mt-4 min-h-12 text-sm leading-relaxed text-slate">{t(`homeFleet.items.${id}.desc`)}</p>
          <div className="mt-6 flex items-center gap-5 font-mono text-[11px] font-semibold uppercase tracking-[0.16em] text-ink/65">
            <span className="flex items-center gap-2">
              <Users className="h-4 w-4 text-miles" strokeWidth={1.5} />
              {t("veh.pax").replace("{n}", vehicle.pax)}
            </span>
            <span className="flex items-center gap-2">
              <Luggage className="h-4 w-4 text-miles" strokeWidth={1.5} />
              {t("veh.bags").replace("{n}", vehicle.bags)}
            </span>
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

export default function FleetShowcase() {
  const { t } = useLang();

  return (
    <section className="relative overflow-hidden bg-cloud py-24 sm:py-32 lg:py-36" data-testid="home-fleet-section">
      <div className="pointer-events-none absolute left-1/2 top-0 h-px w-[72rem] -translate-x-1/2 bg-gradient-to-r from-transparent via-miles/25 to-transparent" />
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid gap-8 lg:grid-cols-[1fr_auto] lg:items-end">
          <motion.div
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.75, ease }}
          >
            <p className="font-mono text-[11px] uppercase tracking-[0.32em] text-miles">{t("homeFleet.eyebrow")}</p>
            <h2 className="mt-6 max-w-3xl text-4xl font-semibold leading-[0.98] tracking-[-0.055em] text-ink sm:text-6xl lg:text-7xl">
              {t("homeFleet.h2a")}
              <br />
              <span className="font-light text-slate/55">{t("homeFleet.h2b")}</span>
            </h2>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.75, delay: 0.12, ease }}
            className="max-w-sm"
          >
            <p className="text-base leading-relaxed text-slate">{t("homeFleet.p")}</p>
            <Link
              to="/flotte"
              data-testid="home-fleet-explore-cta"
              className="group mt-6 inline-flex items-center gap-2 rounded-full bg-ink px-6 py-3 text-sm font-semibold text-white transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-miles"
            >
              {t("homeFleet.cta")}
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" strokeWidth={1.5} />
            </Link>
          </motion.div>
        </div>

        <div className="mt-16 grid gap-5 sm:grid-cols-2 lg:mt-20 lg:grid-cols-4 lg:gap-6 lg:-mx-10 xl:-mx-20" data-testid="home-fleet-grid">
          {ORDER.map((id, index) => (
            <FleetCard key={id} id={id} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
}
